import SubmissionStatusBadge from '@/components/submissions/SubmissionStatusBadge';
import { formatPassRatio, isSubmissionPending } from '@/lib/submissions';
import type { SubmissionResultSummary, SubmissionStatus } from '@/services/types';

interface SubmitResultPanelProps {
  status: SubmissionStatus;
  result?: SubmissionResultSummary;
}

export default function SubmitResultPanel({ status, result }: SubmitResultPanelProps) {
  if (isSubmissionPending(status)) {
    return (
      <div className='bg-blue-50 border border-blue-200 rounded-xl px-4 py-4'>
        <div className='flex items-center justify-between gap-3'>
          <p className='text-blue-700 text-[0.875rem]'>正在判题，请稍候。页面会自动刷新结果。</p>
          <SubmissionStatusBadge status={status} />
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className='bg-orange-50 border border-orange-200 text-orange-700 rounded-xl px-4 py-4 text-[0.875rem]'>
        暂无判题结果，请稍后刷新。
      </div>
    );
  }

  return (
    <div className='bg-white border border-apple-border rounded-xl px-4 py-4 space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-apple-near-black font-medium text-[1rem]'>判题结果</h3>
        <SubmissionStatusBadge status={result.status} />
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <ResultStat label='通过率' value={formatPassRatio(result.passRatio)} />
        <ResultStat label='通过用例' value={`${result.passedCount}/${result.totalCount}`} />
        <ResultStat label='耗时' value={`${result.executionMs} ms`} />
        <ResultStat label='内存' value={`${result.memoryKb} KB`} />
      </div>

      {result.failures.length > 0 ? (
        <div className='bg-red-50 text-red-700 rounded-xl px-3 py-3 text-[0.8125rem]'>
          <p className='font-medium mb-1'>失败摘要</p>
          <ul className='space-y-1'>
            {result.failures.map((failure, index) => (
              <li key={`${failure}-${index}`}>{failure}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

interface ResultStatProps {
  label: string;
  value: string;
}

function ResultStat({ label, value }: ResultStatProps) {
  return (
    <div className='bg-apple-light-gray rounded-lg px-3 py-3'>
      <p className='text-apple-gray text-[0.75rem]'>{label}</p>
      <p className='text-apple-near-black font-semibold text-[0.9375rem] mt-1'>{value}</p>
    </div>
  );
}
